import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Card,
  Row,
  Col,
  Button,
  Badge,
  Form,
  InputGroup,
} from "react-bootstrap";
import {
  FaSearch,
  FaEye,
  FaRupeeSign,
  FaFilter,
  FaSortAmountDown,
} from "react-icons/fa";
import { toast } from "react-toastify";
import Header from "../../../components/common/Header";
import StudentService from "../../../services/studentService";

const StudentManagement = () => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name"); // name, date, due
  const [filterDue, setFilterDue] = useState("all"); // all, due, paid

  const navigate = useNavigate();

  useEffect(() => {
    loadStudents();
  }, []);

  useEffect(() => {
    filterAndSortStudents();
  }, [students, searchTerm, sortBy, filterDue]);

  const loadStudents = async () => {
    try {
      setLoading(true);
      const response = await StudentService.getAllStudents();
      setStudents(response.data || []);
    } catch (error) {
      toast.error("Failed to load students");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortStudents = () => {
    let filtered = [...students];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (s) =>
          s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          s.phone.includes(searchTerm),
      );
    }

    // Apply due filter - FIXED VERSION
    if (filterDue === "due") {
      filtered = filtered.filter((s) => parseFloat(s.total_due) > 0);
    } else if (filterDue === "paid") {
      filtered = filtered.filter((s) => parseFloat(s.total_due) === 0);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "date":
          return new Date(b.created_at) - new Date(a.created_at);
        case "due":
          return parseFloat(b.total_due) - parseFloat(a.total_due);
        default:
          return 0;
      }
    });

    setFilteredStudents(filtered);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div>
      <Header />
      <div className="container-fluid p-4" >
        {/* Header */}
        <Row className="mb-4 align-items-center">
          <Col>
            <h2>
              <FaRupeeSign className="me-2 text-success" />
              Fee Management
            </h2>
            <p className="text-muted mb-0">Manage student fees and payments</p>
          </Col>
        </Row>

        {/* Summary Cards */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="shadow-sm border-0 bg-primary text-white">
              <Card.Body>
                <h6>Total Students</h6>
                <h3>{students.length}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm border-0 bg-success text-white">
              <Card.Body>
                <h6>Total Fees</h6>
                <h3>
                  {formatCurrency(
                    students.reduce((sum, s) => sum + (s.total_fees || 0), 0),
                  )}
                </h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm border-0 bg-info text-white">
              <Card.Body>
                <h6>Total Paid</h6>
                <h3>
                  {formatCurrency(
                    students.reduce((sum, s) => sum + (s.total_paid || 0), 0),
                  )}
                </h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm border-0 bg-warning text-white">
              <Card.Body>
                <h6>Total Due</h6>
                <h3>
                  {formatCurrency(
                    students.reduce((sum, s) => sum + (s.total_due || 0), 0),
                  )}
                </h3>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card className="mb-4 shadow-sm">
          <Card.Body>
            <Row>
              <Col md={5}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search by name, email or phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaFilter />
                  </InputGroup.Text>
                  <Form.Select
                    value={filterDue}
                    onChange={(e) => setFilterDue(e.target.value)}
                  >
                    <option value="all">All Students</option>
                    <option value="due">With Due Amount</option>
                    <option value="paid">Fully Paid</option>
                  </Form.Select>
                </InputGroup>
              </Col>
              <Col md={3}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaSortAmountDown />
                  </InputGroup.Text>
                  <Form.Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="name">Sort by Name</option>
                    <option value="date">Sort by Join Date</option>
                    <option value="due">Sort by Due Amount</option>
                  </Form.Select>
                </InputGroup>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Students Table */}
        <Card className="shadow-sm">
          <Card.Body>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <Table striped hover responsive>
                <thead className="bg-light">
                  <tr>
                    <th>#</th>
                    <th>Student Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Courses</th>
                    <th>Total Fees</th>
                    <th>Paid</th>
                    <th>Due</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="text-center py-4">
                        No students found
                      </td>
                    </tr>
                  ) : (
                    filteredStudents.map((student, index) => (
                      <tr key={student.id}>
                        <td>{index + 1}</td>
                        <td>
                          <strong>{student.name}</strong>
                          <br />
                          <small className="text-muted">ID: {student.id}</small>
                        </td>
                        <td>{student.email}</td>
                        <td>{student.phone}</td>
                        <td>
                          <Badge bg="info">{student.total_courses || 0}</Badge>
                        </td>
                        <td>{formatCurrency(student.total_fees || 0)}</td>
                        <td className="text-success">
                          {formatCurrency(student.total_paid || 0)}
                        </td>
                        <td
                          className={
                            student.total_due > 0 ? "text-danger fw-bold" : ""
                          }
                        >
                          {formatCurrency(student.total_due || 0)}
                        </td>
                        <td>
                          {student.total_due === 0 ? (
                            <Badge bg="success">Paid</Badge>
                          ) : (
                            <Badge bg="warning">Due</Badge>
                          )}
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() =>
                              navigate(`/admin/students/${student.id}`)
                            }
                          >
                            <FaEye className="me-1" /> View
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default StudentManagement;
